/**
 * Top-bar hint that Settings > Automations & notifications is off: a small
 * neutral chip "Automations off" linking to the page. Shown while the
 * practice master switch is off, or the selected office's switch is off;
 * hidden once automations can run. Read-only for everyone (the status route
 * carries no PHI); refetched on any `settings.*` live event and every 5 min.
 */
import { Link } from '@tanstack/react-router';
import { AutomationStatusSchema } from '@velora/contracts/automations';
import { useQuery } from '@tanstack/react-query';
import { PowerOff } from 'lucide-react';
import { api } from '../api.js';
import { useOfficeScope } from '../office/OfficeScope.js';

export const AUTOMATION_STATUS_KEY = ['settings', 'automations', 'status'] as const;

/** The chip text, or null when nothing is off for the current scope. */
export function automationsOffLabel(
  s: { practiceOn: boolean; offices: readonly { officeId: string; on: boolean }[] } | undefined,
  officeId: string | null,
): string | null {
  if (!s) return null;
  if (!s.practiceOn) return 'Automations off';
  if (officeId)
    return s.offices.find((o) => o.officeId === officeId)?.on === false
      ? 'Automations off here'
      : null;
  const off = s.offices.filter((o) => !o.on).length;
  if (off === 0) return null;
  return off === s.offices.length ? 'Automations off' : `Automations off at ${off}`;
}

function useAutomationsOffLabel(): string | null {
  const scope = useOfficeScope();
  const q = useQuery({
    queryKey: AUTOMATION_STATUS_KEY,
    queryFn: ({ signal }) =>
      api.request('/settings/automations/status', { schema: AutomationStatusSchema, signal }),
    refetchInterval: 300_000,
    staleTime: 60_000,
    retry: false,
  });
  const officeId = !scope.isAll && scope.office ? scope.office.id : null;
  return automationsOffLabel(q.data, officeId);
}

const OFF_TITLE =
  'Automations are OFF. Nothing is sent to patients. Open Settings > Automations & notifications.';

/**
 * The status chip segment (Velora Modern). `compact` shows the icon only, with
 * the label as its accessible name and tooltip.
 */
export function AutomationsIndicator({ compact = false }: { compact?: boolean }) {
  const label = useAutomationsOffLabel();
  if (!label) return null;
  return (
    <Link
      to="/settings/automations"
      className="shell-automations-off"
      data-compact={compact ? '' : undefined}
      title={OFF_TITLE}
      aria-label={compact ? label : undefined}
    >
      <PowerOff size={12} aria-hidden />
      {compact ? null : <span className="shell-automations-off__text">{label}</span>}
    </Link>
  );
}

/** The Dentrix status bar cell (no menu-bar chrome in the familiar skins). */
export function AutomationsStatusText() {
  const label = useAutomationsOffLabel();
  if (!label) return null;
  return (
    <Link to="/settings/automations" title={OFF_TITLE} data-testid="status-automations">
      {label}
    </Link>
  );
}
